import { useMemo } from 'react';
import InteractiveMap from './InteractiveMap';
import { getActiveLocationsForDate } from '../utils';
import { useProject } from '../contexts/ProjectDataContext';

const CalendarMapPanel = ({ 
  isOpen, 
  calendarItems, 
  onClose, 
  activeFilters, 
  visibleCalendars = [],
  profile,
  lat: defaultLat, 
  lng: defaultLng, 
  locationName: defaultName,
  mapStartDate,
  mapEndDate,
  selectedTripId,
  trips = [],
  recommendations = [],
  friendFilter = 'all',
  allUsers = [],
  authUser,
  searchRadius,
  customCenter,
  dispatch,
  tagsHierarchy = {},
  availableTags = [],
  getParentTags,
  getChildTags,
  aliases = []
}) => {
  const { songkickConcerts } = useProject();

  const markers = useMemo(() => {
    if (!calendarItems) return [];

    const homeCity = profile?.cities?.[0];
    const allMarkers = [];

    calendarItems.forEach(item => {
      // 1. Standard Event Types & Photos
      if (['event', 'tentative', 'concert', 'restaurant', 'sight', 'photo'].includes(item.type)) {
        const filterId = item.type === 'event' ? 'nytm-event' : `nytm-${item.type}`;
        const isVisible = activeFilters?.includes(filterId) || visibleCalendars?.includes(filterId);
        if (!isVisible) return;

        const itemStart = item.startDate || item.date;
        const itemEnd = item.endDate || item.date || itemStart;
        if (mapStartDate && itemEnd < mapStartDate) return;
        if (mapEndDate && itemStart > mapEndDate) return;

        let finalLat = item.addressLat || item.lat;
        let finalLng = item.addressLng || item.lng;

        if (!finalLat || !finalLng) {
          if (item.timezoneSource === 'home' || !item.timezoneSource) {
            finalLat = homeCity?.lat;
            finalLng = homeCity?.lng;
          }
        }

        if (finalLat && finalLng) {
          allMarkers.push({
            id: item.id,
            lat: finalLat,
            lng: finalLng,
            title: item.title || item.text || 'Untitled',
            venueName: item.venueName || '',
            address: item.address || '',
            city: item.city || '',
            note: item.note || '',
            category: item.type,
            startDate: item.startDate || item.date,
            startTime: item.startTime || '',
            isAllDay: !!item.isAllDay,
            type: item.type,
            placeId: item.placeId
          });
        }
      }

      // 2. Flights and Hotels from Trip objects
      if (item.type === 'trip') {
        const itemStart = item.startDate;
        const itemEnd = item.endDate || itemStart;
        if (mapStartDate && itemEnd < mapStartDate) return;
        if (mapEndDate && itemStart > mapEndDate) return;

        // Flights
        if (activeFilters?.includes('nytm-flight') || visibleCalendars?.includes('nytm-flight')) {
          Object.entries(item.flightData || {}).forEach(([ident, f]) => {
            // Departure Pin
            if (f.originLat && f.originLng) {
              allMarkers.push({
                id: `${item.id}-${ident}-dep`,
                lat: f.originLat,
                lng: f.originLng,
                title: `Departure: ${ident}`,
                venueName: f.origin || '',
                city: f.originCity || '',
                type: 'flight',
                category: 'flight',
                startDate: item.startDate,
                startTime: f.departureTime ? new Date(f.departureTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''
              });
            }
            // Arrival Pin
            if (f.destinationLat && f.destinationLng) {
              allMarkers.push({
                id: `${item.id}-${ident}-arr`,
                lat: f.destinationLat,
                lng: f.destinationLng,
                title: `Arrival: ${ident}`,
                venueName: f.destination || '',
                city: f.destinationCity || '',
                type: 'flight',
                category: 'flight',
                startDate: item.startDate,
                startTime: f.arrivalTime ? new Date(f.arrivalTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''
              });
            }
          });
        }

        // Hotels
        if (activeFilters?.includes('nytm-hotel') || visibleCalendars?.includes('nytm-hotel')) {
          (item.hotels || []).forEach((h, idx) => {
            if (h.lat && h.lng) {
              allMarkers.push({
                id: `${item.id}-hotel-${idx}`,
                lat: h.lat,
                lng: h.lng,
                title: h.name || 'Hotel',
                venueName: h.name || '',
                address: h.address || '',
                type: 'hotel',
                category: 'hotel',
                startDate: h.startDate,
                startTime: h.checkInTime || ''
              });
            }
          });
        }
      }
    });

    return allMarkers;
  }, [calendarItems, activeFilters, visibleCalendars, mapStartDate, mapEndDate, profile]);

  const allFilteredMarkers = useMemo(() => {
    const combined = [...markers];

    const showFood = visibleCalendars?.includes('nytm-restaurant');
    const showDrinks = visibleCalendars?.includes('nytm-drinks');
    const showCoffee = visibleCalendars?.includes('nytm-coffee');
    const showSights = visibleCalendars?.includes('nytm-sight');
    const showShopping = visibleCalendars?.includes('nytm-shopping');
    const showWantToGo = visibleCalendars?.includes('nytm-wanttogo');
    const showMusicVenues = visibleCalendars?.includes('nytm-musicvenue');
    
    // Quick exit if no recommendation calendars are visible
    if (!showFood && !showDrinks && !showCoffee && !showSights && !showShopping && !showWantToGo && !showMusicVenues) {
      return combined;
    }

    const existingPlaceIds = new Set(markers.map(m => m.placeId).filter(Boolean));

    const recsToAdd = recommendations.filter(rec => {
      if (existingPlaceIds.has(rec.placeId)) return false;

      // 1. Check if user matches friendFilter
      let userMatches = false;
      if (friendFilter === 'all') {
        const friendEmails = (profile?.friends || []).map(e => e.toLowerCase());
        const isMyRec = rec.userId === authUser?.uid;
        
        let isFriendRec = false;
        if (!isMyRec) {
           const recUser = allUsers.find(u => u.id === rec.userId);
           if (recUser && recUser.email && friendEmails.includes(recUser.email.toLowerCase())) {
             isFriendRec = true;
           }
        }
        userMatches = isMyRec || isFriendRec;
      } else if (friendFilter === 'me') {
        userMatches = rec.userId === authUser?.uid;
      } else {
        userMatches = rec.userId === friendFilter;
      }

      if (!userMatches) return false;

      // Filter by visible icon categories
      const isFood = rec.tags?.some(t => getParentTags(t).includes('Food') || t === 'Food');
      const isDrinks = rec.tags?.some(t => getParentTags(t).includes('Drink') || t === 'Drink');
      const isCoffee = rec.tags?.some(t => getParentTags(t).includes('Coffee') || t === 'Coffee');
      const isSight = rec.tags?.some(t => getParentTags(t).includes('Sights') || t === 'Sights' || getParentTags(t).includes('Sight') || t === 'Sight');
      const isShopping = rec.tags?.some(t => getParentTags(t).includes('Shopping') || t === 'Shopping');
      const isMusicVenue = rec.tags?.some(t => getParentTags(t).includes('Music Venue') || t === 'Music Venue');
      const isWantToGo = rec.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go');
      
      if (!showFood && !showDrinks && !showCoffee && !showSights && !showShopping && !showMusicVenues) return false;

      const activeTypes = [];
      if (showFood && isFood) activeTypes.push('restaurant');
      if (showDrinks && isDrinks) activeTypes.push('drinks');
      if (showCoffee && isCoffee) activeTypes.push('coffee');
      if (showSights && isSight) activeTypes.push('sight');
      if (showShopping && isShopping) activeTypes.push('shopping');
      if (showMusicVenues && isMusicVenue) activeTypes.push('musicvenue');
      
      if (activeTypes.length === 0) return false;

      // Filter by active tags (advancedFilters passed as activeFilters)
      const selectedTags = activeFilters === null ? [] : activeFilters.filter(f => f.startsWith('tag-')).map(f => f.replace('tag-', ''));
      
      let passesTagFilter = false;
      if (activeFilters === null) {
          passesTagFilter = true;
      } else {
          const tagsByRoot = {
              'restaurant': selectedTags.filter(t => getParentTags(t).includes('Food') || t === 'Food'),
              'drinks': selectedTags.filter(t => getParentTags(t).includes('Drinks') || t === 'Drinks' || getParentTags(t).includes('Drink') || t === 'Drink'),
              'coffee': selectedTags.filter(t => getParentTags(t).includes('Coffee') || t === 'Coffee'),
              'sight': selectedTags.filter(t => getParentTags(t).includes('Sights') || t === 'Sights' || getParentTags(t).includes('Sight') || t === 'Sight'),
              'shopping': selectedTags.filter(t => getParentTags(t).includes('Shopping') || t === 'Shopping'),
              'musicvenue': selectedTags.filter(t => getParentTags(t).includes('Music Venue') || t === 'Music Venue')
          };
          
          for (const type of activeTypes) {
              const tagsForType = tagsByRoot[type];
              if (!tagsForType || tagsForType.length === 0) {
                  // passesTagFilter remains false
              } else {
                  if (tagsForType.some(t => rec.tags?.includes(t))) {
                      passesTagFilter = true;
                      break;
                  }
              }
          }
      }
      if (!passesTagFilter) return false;

      if (showWantToGo !== isWantToGo) return false;
      
      // Store the activeTypes array on the record itself for the next map phase
      rec._activeTypes = activeTypes;

      // 3. Determine active locations and dates to check against
      let datesToCheck = [];
      const getLocationsForDate = (dateStr, dateObj) => {
        return getActiveLocationsForDate(dateStr, dateObj, trips, calendarItems, profile?.cities?.[0]);
      };

      if (customCenter) {
        datesToCheck = [{ lat: customCenter.lat, lng: customCenter.lng, date: new Date() }];
      } else if (mapStartDate && mapEndDate) {
        let curr = new Date(mapStartDate + 'T12:00:00');
        const end = new Date(mapEndDate + 'T12:00:00');
        while (curr <= end) {
          const dStr = curr.toISOString().split('T')[0];
          datesToCheck = datesToCheck.concat(getLocationsForDate(dStr, new Date(curr)));
          curr.setDate(curr.getDate() + 1);
        }
      } else {
        const todayObj = new Date();
        const todayStr = new Date(todayObj.getTime() - (todayObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        datesToCheck = getLocationsForDate(todayStr, todayObj);
      }

      // Check if rec matches any active location AND is open on that day
      let matchFound = false;
      let minDistance = Infinity;
      
      if (isWantToGo) {
        matchFound = true; // Bypass location check for bucket list items
      }
      
      for (const loc of datesToCheck) {
        // Fast bounding box check (1 degree lat ~= 69 miles)
        const latDelta = searchRadius / 69;
        const lonDelta = searchRadius / Math.max(10, 69 * Math.cos(loc.lat * Math.PI / 180));
        if (Math.abs(rec.lat - loc.lat) > latDelta || Math.abs(rec.lng - loc.lng) > lonDelta) {
          continue;
        }

        const R = 3958.8; // miles
        const dLat = (rec.lat - loc.lat) * Math.PI / 180;
        const dLon = (rec.lng - loc.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(loc.lat * Math.PI / 180) * Math.cos(rec.lat * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;

        if (distance <= searchRadius) {
           // Check if open
           let isOpen = true;
           if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
               const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
               const dayName = dayNames[loc.date.getDay()];
               const desc = rec.weekdayDescriptions.find(d => d.startsWith(dayName));
               if (desc && desc.toLowerCase().includes('closed')) {
                 const nowMs = new Date().getTime();
                 const updatedAt = rec.updatedAt?.seconds * 1000 || rec.createdAt?.seconds * 1000 || 0;
                 if (nowMs - updatedAt <= 7 * 24 * 60 * 60 * 1000) {
                   isOpen = false;
                 }
               }
           }
           if (isOpen) {
             matchFound = true;
             if (distance < minDistance) minDistance = distance;
           }
        }
      }
      if (matchFound) rec.minDistance = minDistance;
      return matchFound;
    });

    // Merge recommendations with same placeId (overlap handling)
    const recsByPlace = {};
    recsToAdd.forEach(rec => {
      if (!recsByPlace[rec.placeId]) {
        recsByPlace[rec.placeId] = {
          id: `rec-${rec.placeId}`,
          lat: rec.lat,
          lng: rec.lng,
          title: rec.placeName,
          venueName: rec.placeName,
          address: rec.address || '',
          type: rec._activeTypes && rec._activeTypes.length > 0 ? rec._activeTypes[0] : 'restaurant',
          multiTypes: rec._activeTypes || [],
          category: 'Recommendation',
          isRecommendation: true,
          placeId: rec.placeId,
          tags: new Set(rec.tags || []),
          users: new Set([rec.userId]),
          minDistance: rec.minDistance
        };
      } else {
        recsByPlace[rec.placeId].users.add(rec.userId);
        rec.tags?.forEach(t => recsByPlace[rec.placeId].tags.add(t));
        if (rec.minDistance < recsByPlace[rec.placeId].minDistance) {
          recsByPlace[rec.placeId].minDistance = rec.minDistance;
        }
      }
    });

    Object.values(recsByPlace).forEach(r => {
      r.tags = Array.from(r.tags);
      if (r.users.has(authUser?.uid)) r.category = 'My Recommendation';
      else if (r.users.size > 0) r.category = 'Friend Recommendation';
      
      // Store distance label for UI
      if (r.minDistance !== undefined && r.minDistance !== Infinity) {
        r.distanceLabel = `(${r.minDistance.toFixed(1)} mi)`;
      }
      
      r.isWantToGo = r.tags.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go');
      
      combined.push(r);
    });

    const showConcerts = visibleCalendars?.includes('nytm-concert');
    if (showConcerts) {
      songkickConcerts.forEach(concert => {
        if (concert.isCanceled || !concert.lat || !concert.lng) return;

        let datesToCheck = [];
        const getLocationsForDate = (dateStr, dateObj) => {
          return getActiveLocationsForDate(dateStr, dateObj, trips, calendarItems, profile?.cities?.[0]);
        };

        if (customCenter) {
          datesToCheck = [{ lat: customCenter.lat, lng: customCenter.lng, date: new Date() }];
        } else if (mapStartDate && mapEndDate) {
          let curr = new Date(mapStartDate + 'T12:00:00');
          const end = new Date(mapEndDate + 'T12:00:00');
          while (curr <= end) {
            const dStr = curr.toISOString().split('T')[0];
            datesToCheck = datesToCheck.concat(getLocationsForDate(dStr, new Date(curr)));
            curr.setDate(curr.getDate() + 1);
          }
        } else {
          const todayObj = new Date();
          const todayStr = new Date(todayObj.getTime() - (todayObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
          datesToCheck = getLocationsForDate(todayStr, todayObj);
        }

        let matchFound = false;
        let minDistance = Infinity;

        for (const loc of datesToCheck) {
          // Fast bounding box check
          const latDelta = searchRadius / 69;
          const lonDelta = searchRadius / Math.max(10, 69 * Math.cos(loc.lat * Math.PI / 180));
          if (Math.abs(concert.lat - loc.lat) > latDelta || Math.abs(concert.lng - loc.lng) > lonDelta) {
            continue;
          }

          const R = 3958.8; // miles
          const dLat = (concert.lat - loc.lat) * Math.PI / 180;
          const dLon = (concert.lng - loc.lng) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(loc.lat * Math.PI / 180) * Math.cos(concert.lat * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          const distance = R * c;

          if (distance <= searchRadius) {
            matchFound = true;
            if (distance < minDistance) minDistance = distance;
          }
        }

        if (matchFound) {
          combined.push({
            id: concert.id,
            lat: concert.lat,
            lng: concert.lng,
            title: `${concert.title || concert.artist} (${minDistance.toFixed(1)} mi)`,
            venueName: concert.venue || '',
            address: concert.city || '',
            type: 'concert',
            category: 'Songkick Concert',
            startDate: concert.startDate,
            minDistance: minDistance,
            songkickUrl: concert.songkickUrl,
            artist: concert.artist
          });
        }
      });
    }

    return combined;
  }, [markers, recommendations, friendFilter, activeFilters, profile, allUsers, authUser, defaultLat, defaultLng, searchRadius, customCenter, trips, songkickConcerts]);

  // Center on trip destination if selected
  const activeTrip = useMemo(() => trips.find(t => t.id === selectedTripId), [trips, selectedTripId]);
  const currentLng = customCenter?.lng || activeTrip?.lng || defaultLng;
  const currentLat = customCenter?.lat || activeTrip?.lat || defaultLat;
  const currentLocationName = customCenter?.name || activeTrip?.city || defaultName;

  if (!isOpen) return null;

  return (
    <div className="bg-white border-t border-zinc-200 relative flex flex-col overflow-hidden transition-all duration-300 ease-in-out" style={{ height: '75%' }}>
      <div className="flex-1">
        <InteractiveMap 
          markers={allFilteredMarkers} 
          showRadar={activeFilters?.includes('radar')} 
          lat={currentLat}
          lng={currentLng}
          locationName={currentLocationName}
          recommendations={recommendations}
          aliases={aliases}
          authUser={authUser}
          dispatch={dispatch}
        />
      </div>
    </div>
  );
};

export default CalendarMapPanel;
